/**
 * Breathing session state machine.
 *
 * States: idle → ready → running (inhale/hold/exhale/rest/top_up) → complete
 *
 * Driven from a single requestAnimationFrame loop. Timing is synced with
 * audioContext.currentTime when Web Audio is available, falling back to
 * performance.now() so the visual flow still works without audio.
 *
 * Ported from the standalone Calm The Body app (sessionController.js).
 */

import { playCue, getContext } from './audioController';
import type { Protocol, Phase, PhaseType } from '@/data/calm/protocols';

export type SessionState = 'idle' | 'ready' | 'running' | 'paused' | 'complete';

interface TimelinePhase extends Phase {
  startAt: number;
}

export interface Tick {
  state: 'running' | 'complete';
  phase: TimelinePhase;
  phaseIdx: number;
  phaseElapsed: number;
  phaseRemaining: number;
  elapsed: number;
  totalDuration: number;
  progress: number;
  cycleProgress: number;
  totalPhases?: number;
}

export interface SessionController {
  getPhases: () => TimelinePhase[];
  getTotalDuration: () => number;
  start: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setSound: (enabled: boolean) => void;
  getState: () => SessionState;
  handleVisibility: () => void;
}

/** Clock in seconds: prefers the audio context, falls back to performance.now(). */
function now(): number {
  const ac = getContext();
  if (ac) return ac.currentTime;
  return performance.now() / 1000;
}

/**
 * Create a new breathing session controller.
 *
 * @param protocol     the protocol to run
 * @param durationSec  total session duration in seconds
 * @param onTick       called every frame with session state
 * @param onComplete   called once when the session finishes
 */
export function createSession(
  protocol: Protocol,
  durationSec: number,
  onTick: (tick: Tick) => void,
  onComplete: () => void,
): SessionController {
  const phases = buildTimeline(protocol, durationSec);
  const totalDuration = phases.reduce((sum, p) => sum + p.seconds, 0);

  let state: SessionState = 'idle';
  let startTime = 0;
  let pauseOffset = 0;
  let pauseStart = 0;
  let rafId: number | null = null;
  let currentPhaseIdx = -1;
  let soundEnabled = true;
  let completed = false;

  /** Build a flat timeline of phases from intro + repeated main cycles. */
  function buildTimeline(proto: Protocol, duration: number): TimelinePhase[] {
    const timeline: TimelinePhase[] = [];

    if (proto.introPhases && proto.introPhases.length > 0) {
      for (const p of proto.introPhases) {
        timeline.push({ ...p, startAt: 0 });
      }
    }

    const introDuration = timeline.reduce((s, p) => s + p.seconds, 0);
    const mainCycleDuration = proto.mainPhases.reduce((s, p) => s + p.seconds, 0);
    let remaining = duration - introDuration;

    if (mainCycleDuration > 0) {
      while (remaining >= mainCycleDuration) {
        for (const p of proto.mainPhases) {
          timeline.push({ ...p, startAt: 0 });
        }
        remaining -= mainCycleDuration;
      }
    }

    // Ensure at least one full main cycle even on very short durations.
    if (timeline.length === 0 && proto.mainPhases.length > 0) {
      for (const p of proto.mainPhases) {
        timeline.push({ ...p, startAt: 0 });
      }
    }

    let cumulative = 0;
    for (const phase of timeline) {
      phase.startAt = cumulative;
      cumulative += phase.seconds;
    }

    return timeline;
  }

  /** Find the current phase index for a given elapsed time. */
  function getPhaseAtTime(elapsed: number): number {
    for (let i = phases.length - 1; i >= 0; i--) {
      if (elapsed >= phases[i].startAt) return i;
    }
    return 0;
  }

  /** The main animation/timing loop. */
  function tick(): void {
    if (state !== 'running') return;

    const elapsed = now() - startTime - pauseOffset;

    if (elapsed >= totalDuration) {
      state = 'complete';
      const lastPhase = phases[phases.length - 1];
      onTick({
        state: 'complete',
        phase: lastPhase,
        phaseIdx: phases.length - 1,
        phaseElapsed: lastPhase.seconds,
        phaseRemaining: 0,
        elapsed: totalDuration,
        totalDuration,
        progress: 1,
        cycleProgress: 1,
      });
      if (!completed) {
        completed = true;
        onComplete();
      }
      return;
    }

    const idx = getPhaseAtTime(elapsed);
    const phase = phases[idx];
    const phaseElapsed = elapsed - phase.startAt;
    const phaseRemaining = phase.seconds - phaseElapsed;

    if (idx !== currentPhaseIdx) {
      currentPhaseIdx = idx;
      if (soundEnabled && phase.type !== 'rest') {
        playCue(phase.type as PhaseType, 0.5);
      }
    }

    const cycleProgress = phase.seconds > 0 ? phaseElapsed / phase.seconds : 0;

    onTick({
      state: 'running',
      phase,
      phaseIdx: idx,
      phaseElapsed,
      phaseRemaining,
      elapsed,
      totalDuration,
      progress: totalDuration > 0 ? elapsed / totalDuration : 0,
      cycleProgress,
      totalPhases: phases.length,
    });

    rafId = requestAnimationFrame(tick);
  }

  return {
    getPhases: () => phases,
    getTotalDuration: () => totalDuration,

    start() {
      if (state !== 'idle' && state !== 'ready') return;
      startTime = now();
      pauseOffset = 0;
      currentPhaseIdx = -1;
      completed = false;
      state = 'running';
      rafId = requestAnimationFrame(tick);
    },

    pause() {
      if (state !== 'running') return;
      state = 'paused';
      pauseStart = now();
      if (rafId !== null) cancelAnimationFrame(rafId);
    },

    resume() {
      if (state !== 'paused') return;
      pauseOffset += now() - pauseStart;
      pauseStart = 0;
      state = 'running';
      const ac = getContext();
      if (ac && ac.state === 'suspended') {
        ac.resume().catch(() => {
          /* ignore */
        });
      }
      rafId = requestAnimationFrame(tick);
    },

    stop() {
      state = 'idle';
      if (rafId !== null) cancelAnimationFrame(rafId);
      currentPhaseIdx = -1;
    },

    setSound(enabled: boolean) {
      soundEnabled = enabled;
    },

    getState() {
      return state;
    },

    handleVisibility() {
      if (state === 'running') {
        const ac = getContext();
        if (ac && ac.state === 'suspended') {
          ac.resume().catch(() => {
            /* ignore */
          });
        }
      }
    },
  };
}
